import type {
  AggregateConstraintValidation,
  AllocationConstraintType,
  AverageValueConstraintType,
  BuyUniverseConstraintType,
  CashflowBandConstraintType,
  DiversificationConstraintType,
  LadderPolicyConstraint,
  OrderLimitConstraintType,
  PiecewiseConstraintType,
  TradeConstraintType,
  UniverseConstraintType,
} from '@moment/horizon-data-access/proposal'
import type { FilterObject } from '@moment/trading-types/filter-expression'
import type { z } from 'zod'
import type { ValueUnit } from '@/types/enums/value-unit'

import { AggregateConstraintValidationType } from '@moment/horizon-data-access/proposal'

export enum ModelConstraintType {
  Universe = 'universe',
  BuyUniverse = 'buy_universe',
  Diversification = 'diversification',
  LadderPolicy = 'ladder_policy',
  Allocation = 'allocation',
  AverageValue = 'average_value',
  Trade = 'trade',
  Piecewise = 'piecewise',
  OrderLimit = 'order_limit',
  CashflowBand = 'cashflow_band',
}

export enum ComplianceStatus {
  Satisfied = 'satisfied',
  Violation = 'violation',
  New = 'new',
}

// Unit for a trade constraint's min/max bounds. Backend defaults to market_value.
type TradeSizeBasis = 'market_value' | 'par'

export interface PiecewiseCondition {
  account_market_value?: ConstraintFilterCondition
}

export interface Piece {
  condition: PiecewiseCondition
  constraints: ConcreteConstraint[]
}

export interface PiecewiseConstraint {
  id?: string
  type: PiecewiseConstraintType
  pieces: Piece[]
  constraint_priority?: number | null
  scope?: FilterObject
}

// Concrete constraint types are non-nested constraints (most notably, ladder policy and piecewise constraints are nested)
type ConcreteConstraint =
  | UniverseConstraint
  | DiversificationConstraint
  | AllocationConstraint
  | AverageValueConstraint
  | TradeConstraint
  | BuyUniverseConstraint

export type ModelConstraint = { citation?: string } & (
  | UniverseConstraint
  | DiversificationConstraint
  | LadderPolicyConstraint
  | AllocationConstraint
  | AverageValueConstraint
  | TradeConstraint
  | BuyUniverseConstraint
  | PiecewiseConstraint
  | OrderLimitConstraint
  | CashflowBandConstraint
)

export type ModelConstraintValidation =
  | UniverseConstraintValidation
  | DiversificationConstraintValidation
  | LadderPolicyConstraintValidation
  | AllocationConstraintValidation
  | AverageValueConstraintValidation
  | BuyUniverseConstraintValidation
  | PiecewiseConstraintValidation
  | CashflowBandConstraintValidation

export type ConstraintValidation =
  | ModelConstraintValidation
  | AggregateConstraintValidation

export function isAggregateValidation(
  validation: ConstraintValidation,
): validation is AggregateConstraintValidation {
  return (
    'type' in validation &&
    validation.type === AggregateConstraintValidationType.aggregate
  )
}

export type EqualWeightConstraintValidation =
  DiversificationConstraintValidation & {
    constraint: DiversificationConstraint & { equal_weight_tolerance: number }
  }

export function isEqualWeightValidation(
  validation: ConstraintValidation,
): validation is EqualWeightConstraintValidation {
  const constraint: unknown =
    'constraint' in validation ? validation.constraint : undefined
  return (
    typeof constraint === 'object' &&
    constraint !== null &&
    'equal_weight_tolerance' in constraint &&
    constraint.equal_weight_tolerance != null
  )
}

export interface ConstraintFilterCondition {
  equal_to?: string | number | boolean
  not_equal_to?: string | boolean
  greater_than?: string | number
  less_than?: string | number
  greater_than_or_equal_to?: string | number
  less_than_or_equal_to?: string | number
  in?: Array<string | number | boolean>
  not_in?: Array<string | number | boolean>
  in_filter_list?: string
  not_in_filter_list?: string
  is_null?: boolean
  includes?: string
  not_includes?: string
  starts_with?: string
  not_starts_with?: string
  ends_with?: string
}

export type { LadderPolicyConstraint }

export interface LadderPolicyConstraintValidation {
  compliant: boolean
  constraint: LadderPolicyConstraint
  additional_constraints?: (
    | AllocationConstraintValidation
    | UniverseConstraintValidation
  )[]
  relaxed: boolean
  relaxed_compliant: boolean
}

export interface UniverseConstraint {
  id?: string // TODO: Change this to required once ProposalBuilderV1 is deprecated
  type: UniverseConstraintType
  filter: FilterObject
  scope?: FilterObject
  constraint_priority?: number | null
}

export interface UniverseConstraintValidation {
  compliant: boolean
  constraint: UniverseConstraint
  holdings?: Array<{
    instrument_id: string
    weight: number
    market_value: number
    compliant: boolean
  }>
  relaxed?: boolean
  relaxed_compliant?: boolean | null
}

export interface DiversificationConstraint {
  id?: string // TODO: Change this to required once ProposalBuilderV1 is deprecated
  type: DiversificationConstraintType
  field: string
  max_weight_per_unique_value?: number
  max_market_value_per_unique_value?: number
  min_weight_per_unique_value?: number
  min_market_value_per_unique_value?: number
  max_instruments_per_unique_value?: number
  min_instruments_per_unique_value?: number
  min_unique_fields?: number
  max_unique_fields?: number
  constraint_priority?: number | null
  // Every held group within ±tolerance of one common size, e.g. 0.1 = ±10%. Must be the rule's only limit.
  equal_weight_tolerance?: number
  scope?: FilterObject
}

export interface DiversificationConstraintValidation {
  compliant: boolean
  constraint: DiversificationConstraint
  fields?: Array<{
    field_value: string
    holdings?: Array<string>
    market_value: number
    weight: number
    compliant: boolean
    equal_weight_deviation?: EqualWeightDeviation | null
  }>
  relaxed: boolean
  relaxed_compliant: boolean
  equal_weight_target?: EqualWeightTarget | null
}

export type EqualWeightDeviation = 'too_small' | 'too_large'

// The weight range, as fractions of the account, every held group should fall in.
export interface EqualWeightTarget {
  min_weight: number
  max_weight: number
}

export interface AverageValueConstraint {
  type: AverageValueConstraintType
  field: string
  min_average_value?: number | string | null
  target_average_value?: number | string | null
  max_average_value?: number | string | null
  constraint_priority?: number | null
  scope?: FilterObject
  id?: string
}

export interface AverageValueConstraintValidation {
  compliant: boolean
  constraint: AverageValueConstraint
  holdings_average_value?: string | number
  holdings_in_bounds?: Array<string>
  relaxed: boolean
  relaxed_compliant: boolean
}

export interface AllocationConstraint {
  id?: string // TODO: Change this to required once ProposalBuilderV1 is deprecated
  type: AllocationConstraintType
  filter: FilterObject
  min_weight?: number
  max_weight?: number
  target_weight?: number
  min_market_value?: number
  max_market_value?: number
  target_market_value?: number
  constraint_priority?: number | null
  scope?: FilterObject
}

export interface AllocationConstraintValidation {
  compliant: boolean
  constraint: AllocationConstraint
  market_value?: number
  weight?: number
  holdings_in_allocation?: Array<string>
  relaxed: boolean
  relaxed_compliant: boolean
}

export interface TradeConstraint {
  id?: string
  type: TradeConstraintType
  min_size: number
  // Optional: omitted when there's no upper trade bound.
  max_size?: number
  size_basis?: TradeSizeBasis
  constraint_priority?: number | null
}

export interface CashflowBandConstraint {
  id?: string
  type: CashflowBandConstraintType
  // Half-width of the band as a fraction of the target's mean bucket, in (0, 1].
  max_deviation_ratio: number
  constraint_priority?: number | null
}

export interface CashflowBandConstraintValidation {
  compliant: boolean
  constraint: CashflowBandConstraint
  relaxed?: boolean
  relaxed_compliant?: boolean | null
}

export interface OrderLimitConstraint {
  id?: string
  type: OrderLimitConstraintType
  max_buy_orders?: number
  max_sell_orders?: number
  max_total_orders?: number
  min_buy_orders?: number
  min_sell_orders?: number
  min_total_orders?: number
  constraint_priority?: number | null
  scope?: FilterObject
}

export interface BuyUniverseConstraint {
  id?: string
  type: BuyUniverseConstraintType
  filter: FilterObject
  constraint_priority?: number | null
}

interface BuyUniverseConstraintValidation {
  compliant: boolean
  constraint: BuyUniverseConstraint
  relaxed?: boolean
  relaxed_compliant?: boolean | null
}

export interface PiecewiseConstraintValidation {
  id?: string
  compliant: boolean
  constraint: PiecewiseConstraint
  additional_constraints: (
    | AllocationConstraintValidation
    | UniverseConstraintValidation
    | DiversificationConstraintValidation
    | AverageValueConstraintValidation
    | BuyUniverseConstraintValidation
  )[]
  relaxed: boolean
  relaxed_compliant: boolean
}

export interface FilterDictionaryFilter {
  [key: string]: ConstraintFilterCondition
}

/**
 *  Types for constraint validators
 */

// Extended AverageValueConstraint type that includes unit property
export type ExtendedAverageValueConstraint = AverageValueConstraint & {
  unit?: ValueUnit | string
}

// Validation result type for constraint validations
export interface ValidationResult {
  // Whether the validation passed
  valid: boolean
  // Error messages if validation failed
  errors?: string[]
}

// Types for constraint validation functions
type ValidateConstraintFn<T> = (constraint: T) => ValidationResult

// Interface for validators
export interface ConstraintValidator<T> {
  validate: ValidateConstraintFn<T>
  schema: z.ZodType<T>
}

// Type for a map of constraint validators by constraint type
export type ConstraintValidatorMap = {
  [ModelConstraintType.AverageValue]?: ConstraintValidator<ExtendedAverageValueConstraint>
  [ModelConstraintType.Allocation]?: ConstraintValidator<AllocationConstraint>
  [ModelConstraintType.Diversification]?: ConstraintValidator<DiversificationConstraint>
  [ModelConstraintType.Universe]?: ConstraintValidator<UniverseConstraint>
  [ModelConstraintType.LadderPolicy]?: ConstraintValidator<LadderPolicyConstraint>
  [ModelConstraintType.Trade]?: ConstraintValidator<TradeConstraint>
  [ModelConstraintType.BuyUniverse]?: ConstraintValidator<BuyUniverseConstraint>
  [ModelConstraintType.Piecewise]?: ConstraintValidator<PiecewiseConstraint>
  [ModelConstraintType.OrderLimit]?: ConstraintValidator<OrderLimitConstraint>
  [ModelConstraintType.CashflowBand]?: ConstraintValidator<CashflowBandConstraint>
}

// Specific AverageValueConstraint validation interface
export interface AverageValueValidationResult extends ValidationResult {
  minValid?: boolean
  maxValid?: boolean
  targetValid?: boolean
  relationshipsValid?: boolean
}
